import { AppState, Table, TableStatus, UserRole } from '../types';

// API Response Type
interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// Normalize table data to ensure all required properties exist
function normalizeTable(table: any): Table {
  return {
    id: table.id || 0,
    number: table.number || table.id?.toString() || '0',
    status: table.status || TableStatus.AVAILABLE,
    actions: {
      kitchenStarted: table.actions?.kitchenStarted || false,
      pizzaConfirmed: table.actions?.pizzaConfirmed || false,
      kitchenReady: table.actions?.kitchenReady || false,
      pizzaReady: table.actions?.pizzaReady || false,
      cancelledBy: table.actions?.cancelledBy
    },
    actionTimestamps: table.actionTimestamps || {},
    lastUpdated: table.lastUpdated || new Date().toISOString(),
    updatedBy: table.updatedBy || UserRole.KITCHEN
  };
}

// Normalize app state to ensure all tables have complete data
function normalizeAppState(state: any): AppState {
  return {
    tables: (state.tables || []).map(normalizeTable),
    totalTables: state.totalTables || state.tables?.length || 0,
    lastSync: state.lastSync || state.lastUpdated || new Date().toISOString(),
    version: state.version || 1
  };
}

// API Configuration - Dynamisch für externe Geräte
const getApiBaseUrl = (): string => {
  // Prüfe ob wir lokal sind (localhost oder 127.0.0.1)
  const isLocalhost = window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1' ||
                     window.location.hostname.startsWith('192.168.') ||
                     window.location.hostname.startsWith('10.');
  
  if (isLocalhost) {
    // In Development: Verwende den aktuellen Host, aber Port 7071 für Functions
    const currentHost = window.location.hostname;
    return `http://${currentHost}:7071/api`;
  }
  return '/api';  // Production: Relative URL für Azure Static Web Apps
};

const API_BASE_URL = getApiBaseUrl();

class ApiService {
  private async makeRequest<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const defaultOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      console.log(`🌐 API Request: ${options.method || 'GET'} ${url}`);
      
      const response = await fetch(url, defaultOptions);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const apiResponse: ApiResponse<T> = await response.json();
      
      if (!apiResponse.success) {
        throw new Error(apiResponse.error || 'API request failed');
      }

      console.log(`✅ API Success: ${options.method || 'GET'} ${endpoint}`);
      return apiResponse.data as T;
      
    } catch (error) {
      console.error(`❌ API Error: ${options.method || 'GET'} ${endpoint}`, error);
      throw new Error(`API request failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Load current table state
  async loadAppState(): Promise<AppState | null> {
    try {
      const rawState = await this.makeRequest<any>('/tables');
      const normalizedState = normalizeAppState(rawState);
      console.log(`📥 Loaded app state version ${normalizedState.version} with ${normalizedState.tables.length} tables`);
      return normalizedState;
    } catch (error) {
      console.error('Failed to load app state:', error);
      throw error;
    }
  }

  // Save table state
  async saveAppState(appState: AppState): Promise<AppState> {
    try {
      const savedState = await this.makeRequest<AppState>('/tables', {
        method: 'POST',
        body: JSON.stringify(appState),
      });
      console.log(`💾 Saved app state version ${savedState.version}`);
      return savedState;
    } catch (error) {
      console.error('Failed to save app state:', error);
      throw error;
    }
  }

  // Check for updates
  async checkForUpdates(currentVersion: number): Promise<{ hasUpdates: boolean; latestVersion: number }> {
    try {
      const result = await this.makeRequest<{ hasUpdates: boolean; latestVersion: number }>
        (`/tables/version/${currentVersion}`);
      
      if (result.hasUpdates) {
        console.log(`🔄 Updates available: ${currentVersion} → ${result.latestVersion}`);
      }
      
      return result;
    } catch (error) {
      console.warn('Failed to check for updates:', error);
      return { hasUpdates: false, latestVersion: currentVersion };
    }
  }

  // Reset all tables
  async resetTables(): Promise<AppState> {
    try {
      const rawState = await this.makeRequest<any>('/tables/reset', {
        method: 'POST',
      });
      const normalizedState = normalizeAppState(rawState);
      console.log(`🔄 Tables reset - new version ${normalizedState.version}`);
      return normalizedState;
    } catch (error) {
      console.error('Failed to reset tables:', error);
      throw error;
    }
  }

  // Health check
  async checkHealth(): Promise<{ status: string; storage: { success: boolean; message: string } }> {
    try {
      const health = await this.makeRequest<{ status: string; storage: { success: boolean; message: string } }>('/health');
      console.log(`🏥 Health check: ${health.status}`);
      return health;
    } catch (error) {
      console.error('Health check failed:', error);
      throw error;
    }
  }

  // Test if API is available
  async isAvailable(): Promise<boolean> {
    try {
      await this.checkHealth();
      return true;
    } catch (error) {
      console.warn('API not available:', error);
      return false;
    }
  }
}

// Singleton instance
export const apiService = new ApiService();
export default apiService;